"use client";

import { useCallback, useState } from "react";
import type { Sound } from "@/lib/supabase";
import { useDownload } from "@/hooks/useDownload";
import { recordPostDownloadConfirmation } from "@/lib/post-download-confirmation";
import {
  trackDownloadButtonClick,
  trackDownloadInitiated,
  trackDownloadCompleted,
  trackReferralLinkGenerated,
  trackReferralLinkCopied,
} from "@/lib/tracking";
import type { DownloadSource } from "@/lib/funnel-property-enums";
import { buildSurfaceShareUrl } from "@/lib/social-share-copy";
import { announce } from "@/lib/announce";
import { downloadFailureMessage } from "@/lib/state-copy";
import { clearRowError, reportRowError, useRowError } from "@/lib/row-error-store";
import { closeRowReceipt, openRowReceipt, useRowReceiptOpen } from "@/lib/row-receipt-store";
import { TESLA_FACTS } from "@/lib/tesla-facts";

const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, "") || "https://teslalocksound.com";

export function useCardDownload(
  sound: Sound,
  previewCompleted: boolean,
  /** Reported as the download source in place of the card's own. Embeds outside the library pass theirs. */
  sourceOverride?: DownloadSource,
) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadDone, setDownloadDone] = useState(false);
  const downloadError = useRowError(sound.id);
  const receiptOpen = useRowReceiptOpen(sound.id);
  const { download: downloadFile } = useDownload();

  const handleCardDownload = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isDownloading || downloadDone) return;
      // W3-1: attribute download to the completion CTA placement when it
      // was the preview reaching ≥80% that surfaced the button prominently.
      const dlSource: DownloadSource = sourceOverride ?? (previewCompleted ? 'preview_completion_cta' : 'sound_card');
      trackDownloadButtonClick({ sound_id: sound.id, sound_title: sound.title, category: sound.category, source: dlSource });
      trackDownloadInitiated({ sound_id: sound.id, sound_title: sound.title, category: sound.category, source: dlSource });
      setIsDownloading(true);
      clearRowError();
      try {
        await downloadFile({
          fileUrl: sound.file_url,
          soundId: sound.id,
          accountingTarget: { kind: "sound", soundId: sound.id },
          source: dlSource,
          onSuccess: () => {
            clearRowError();
            trackDownloadCompleted({ sound_id: sound.id, sound_title: sound.title, category: sound.category, source: dlSource });
            setDownloadDone(true);
            setTimeout(() => setDownloadDone(false), 5000);
            // The checkmark reverts after 5s; the install receipt under the
            // row stays until it is closed (RowReceipt).
            openRowReceipt(sound.id);
            announce(
              `${TESLA_FACTS.usb.filename} for ${sound.title} downloaded. Install steps are open below the sound.`,
              "polite",
            );
            // Arms /guide attribution + reports the impression (TES-1577).
            // The card confirms inline (rule 2: the row's own completion
            // state), so this records without toasting and reports the
            // surface honestly as 'sound_card_inline', not a toast.
            recordPostDownloadConfirmation({
              soundId: sound.id,
              soundTitle: sound.title,
              soundSlug: sound.slug,
              source: dlSource,
              surface: 'sound_card_inline',
            });
          },
          onError: (error) => {
            const message = downloadFailureMessage(error);
            setIsDownloading(false);
            reportRowError(sound.id, message);
            announce(message, "polite");
          },
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [isDownloading, downloadDone, sound, downloadFile, previewCompleted, sourceOverride],
  );

  // TES-1768: a dismissible way to share the sound right after the download
  // completes. Reuses the save-heart's 44px slot for the ~5s completion
  // window (see CardActions) so the row adds zero CLS; it simply reverts to
  // the heart when `downloadDone` clears.
  const handleShare = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const shareUrl = buildSurfaceShareUrl(`${SITE_URL}/sounds/${sound.slug ?? sound.id}`, "sound_card_toast");
      trackReferralLinkGenerated({ sound_id: sound.id, share_surface: "sound_card_toast" });
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        try {
          await navigator.share({ title: `${sound.title}: Tesla Lock Sound`, url: shareUrl });
          trackReferralLinkCopied({ sound_id: sound.id, share_surface: "sound_card_toast", share_method: "native_share" });
        } catch (error) {
          if ((error as DOMException)?.name === "AbortError") return;
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        trackReferralLinkCopied({ sound_id: sound.id, share_surface: "sound_card_toast", share_method: "clipboard" });
      } catch {
        // Clipboard blocked (permissions / insecure context) — no-op.
      }
    },
    [sound.id, sound.slug, sound.title],
  );

  const dismissReceipt = useCallback(() => closeRowReceipt(sound.id), [sound.id]);

  return { isDownloading, downloadDone, downloadError, receiptOpen, dismissReceipt, handleCardDownload, handleShare };
}
